import React, { useRef, useState } from "react";
import { Row, Col, Form, Image, Button } from "react-bootstrap";
import { Card, UploadButton } from "../../index.style";
import { faCloudArrowUp } from "@fortawesome/free-solid-svg-icons";
import {
  AddLessonBar,
  Divider,
  FilesList,
  Header,
  IconButtons,
  SectionLabel,
} from "./TrackLessonsFormCard.style";
import Trash from "assets/images/defaults/trash.png";
import Paste from "assets/images/defaults/paste.png";
import Move from "assets/images/defaults/move.png";
import { faChevronUp, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FileItem } from "components/FileItem";
import { VideoItem } from "components/VideoItem";
import { useLessonVideoUpload } from "../../hooks/useLessonVideoUpload";
import { Input } from "iebt-zing-storybook";
import { toast } from "react-toastify";
import { InputGroup } from "react-bootstrap";
import { faPaste, faEraser } from "@fortawesome/free-solid-svg-icons";

enum VideProviderInputType {
  url,
  upload,
}
const VideoProviders = {
  Cloudflare: {
    label: "Upload de arquivo",
    type: VideProviderInputType.upload,
    formatVideoUrl: (videoId: string): string => "",
    isValid: (url: string): boolean => true,
  },
  Youtube: {
    label: "Link do Youtube",
    type: VideProviderInputType.url,
    invalidMessage: "Informe um link válido do youtube",
    formatVideoUrl: (videoId: string): string =>
      `https://www.youtube.com/watch?v=${videoId}`,
    isValid: (url: string): boolean => {
      if (url && url.trim() === "") return false;
      if (url == null) return true; //novo registro
      const regex =
        /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|shorts\/)|youtu\.be\/)[a-zA-Z0-9_-]{11}(\S*)?$/;

      return regex.test(url.trim());
    },
  },
};

const getVideoProviderType = (videoProvider: string) => {
  return VideoProviders[videoProvider]?.type ?? {};
};

export const videoProviderAllowInputUrl = (videoProvider: string) => {
  return getVideoProviderType(videoProvider) === VideProviderInputType.url;
};

export const formatVideoUrl = (videoProvider: string, videoId: string) => {
  const provider = VideoProviders[videoProvider];
  if (provider === null) return null;
  return provider.formatVideoUrl(videoId);
};

export const validateLesson = (lesson: Lesson) => {
  if (
    lesson.title.trim().length === 0 ||
    lesson.description.trim().length === 0
  )
    return false;

  const provider = VideoProviders[lesson.videoProvider];
  if (provider === null) return true;

  return provider.isValid(lesson.videoUrl ?? "");
};

export type Lesson = {
  id: string | number;
  title: string;
  description: string;
  videoUrl?: string;
  videoFile?: File;
  videoId?: string;
  videoProvider: string;
  minutes?: number;
  sequence?: number;
  attachments?: File[];
  allowFiles?: boolean;
  allowQuestions?: boolean;
  originalVideoId?: string;
  originalvideoProvider?: string;
};

type LessonCardProps = {
  index: number;
  lesson: Lesson;
  onChange: (patch: Partial<Lesson>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onAddLesson?: () => void;
};

export const TrackLessonsFormCard: React.FC<LessonCardProps> = ({
  index,
  lesson,
  onChange,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDelete,
  onAddLesson,
}) => {
  const uploadRef = useRef<HTMLInputElement | null>(null);
  const filesUploadRef = useRef<HTMLInputElement | null>(null);
  const videoUpload = useLessonVideoUpload();
  const [touchedFields, setTouchedFields] = useState<{
    title: boolean;
    description: boolean;
  }>({
    title: false,
    description: false,
  });

  const isUploading = videoUpload.isUploading;

  const selectFiles = () => uploadRef.current?.click();
  const selectAttachmentFiles = () => filesUploadRef.current?.click();

  const onFilesSelected = async (files?: FileList | null) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    const videos = arr.filter((file) => file.type.startsWith("video/"));
    if (videos.length > 0) {
      const videoFile = videos[0];
      onChange({ videoFile });
    }
  };

  const onAttachmentFilesSelected = (files?: FileList | null) => {
    if (!files || files.length === 0) return;
    const newFiles = Array.from(files);
    const currentAttachments = lesson.attachments || [];
    const updatedAttachments = [...currentAttachments, ...newFiles];
    onChange({ attachments: updatedAttachments });
  };

  const removeAttachment = (i: number) => {
    const next = (lesson.attachments ?? []).filter((_, idx) => idx !== i);
    onChange({ attachments: next });
  };

  const removeVideo = () => {
    onChange({ videoFile: undefined, videoId: undefined });
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ title: e.target.value });
    if (!touchedFields.title) {
      setTouchedFields((prev) => ({ ...prev, title: true }));
    }
  };

  const handleVideoUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ videoUrl: e.target.value });
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    onChange({ description: e.target.value });

    if (!touchedFields.description) {
      setTouchedFields((prev) => ({ ...prev, description: true }));
    }
  };

  const handleTitleBlur = () => {
    setTouchedFields((prev) => ({ ...prev, title: true }));
  };

  const handleDescriptionBlur = () => {
    setTouchedFields((prev) => ({ ...prev, description: true }));
  };

  function VideoProviderRadio() {
    const handleChange = (providerId: string) => {
      onChange({
        videoProvider: providerId,
      });
    };

    function generateId() {
      return (
        Date.now().toString(36) +
        Math.random().toString(36).slice(2)
      );
    }

    const videoProviderName = `videoProvider${lesson.sequence}`;

    return (
      <Form>
        <Form.Label className="fw-semibold">Tipo do Vídeo</Form.Label>
        {Object.entries(VideoProviders).map(([providerId, config]) => (
          <Form.Check
            key={providerId}
            id={`video-provider-${generateId()}`}  //faz com que ao clicar no label o radio seja marcado
            type="radio"
            name={videoProviderName}
            label={config.label}
            checked={lesson.videoProvider === providerId}
            onChange={() => handleChange(providerId)}
            className="mb-2"
          />
        ))}
      </Form>
    );
  }

  function VideoProviderField() {
    const provider = VideoProviders[lesson.videoProvider];
    const originalProvider =
      VideoProviders[lesson.originalvideoProvider ?? ""] ?? {};

    function isOriginalUpload() {
      return (
        lesson.videoId && originalProvider.type === VideProviderInputType.upload
      );
    }

    const handlePaste = async () => {
      try {
        const text = await navigator.clipboard.readText();
        onChange({ videoUrl: text });
      } catch (err) {
        console.error("Erro ao acessar a área de transferência", err);
      }
    };

    if (provider.type === VideProviderInputType.url) {
      const hasFilled = !!(lesson.videoUrl && lesson.videoUrl.trim() !== "");

      const sharedInputProps = {
        value: lesson.videoUrl ?? "",
        placeholder: "Cole aqui o link do youtube",
        isInvalid: !provider.isValid(lesson.videoUrl),
        onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
          const pasted = e.clipboardData.getData("text");
          onChange({ videoUrl: pasted });
        },
        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === "Delete" || e.key === "Backspace") {
            onChange({ videoUrl: "" });
          }
        },
      };

      return (
        <div>
          <Form.Label className="fw-semibold">
            Adicione o link da video aula
            <span className="text-danger">*</span>
          </Form.Label>

          <div className="d-md-none">
            <InputGroup className="mb-2">
              <Form.Control
                {...sharedInputProps}
                style={{ wordBreak: "break-all" }}
              />
              <Form.Control.Feedback type="invalid">
                {provider.invalidMessage}
              </Form.Control.Feedback>
            </InputGroup>

            <div className="d-flex" style={{ gap: 8 }}>
              <Button
                onClick={handlePaste}
                title="Colar da área de transferência"
                style={{ flex: 1, opacity: 0.5 }}
              >
                <FontAwesomeIcon icon={faPaste} />
                <span style={{ paddingLeft: 8 }}>Colar</span>
              </Button>
              <Button
                onClick={() => onChange({ videoUrl: "" })}
                title="Limpar link"
                disabled={!lesson.videoUrl}
                style={{ flex: 1, opacity: 0.5 }}
              >
                <FontAwesomeIcon icon={faEraser} />
              </Button>
            </div>
          </div>

          {/* Desktop layout: horizontal InputGroup */}
          <InputGroup className="d-none d-md-flex">
            <Form.Control {...sharedInputProps} />
            <Button
              onClick={handlePaste}
              title="Colar da área de transferência"
              style={{ opacity: 0.5 }}
            >
              <FontAwesomeIcon icon={faPaste} />
              <span style={{ paddingLeft: 10, margin: 3 }}>Colar</span>
            </Button>
            <Button
              onClick={() => onChange({ videoUrl: "" })}
              title="Limpar link"
              disabled={!lesson.videoUrl}
              style={{ opacity: 0.5 }}
            >
              <FontAwesomeIcon icon={faEraser} />
            </Button>
            <Form.Control.Feedback type="invalid">
              {provider.invalidMessage}
            </Form.Control.Feedback>
          </InputGroup>
        </div>
      );
    }

    if (provider.type === VideProviderInputType.upload) {
      return (
        <Form>
          <input
            ref={uploadRef}
            type="file"
            accept="video/mp4,video/webm,video/ogg,video/x-msvideo,video/quicktime,.mp4,.mov,.avi,.wmv,.mkv,.webm,.flv,.m4v"
            hidden
            onChange={(e) => onFilesSelected(e.target.files)}
          />

          <Form.Label className="fw-semibold">Adicione a video aula</Form.Label>

          {isOriginalUpload() && (
            <div className="mb-3">
              <VideoItem
                videoId={lesson.videoId ?? ""}
                title={`Aula ${index + 1}`}
                onRemove={removeVideo}
                showRemoveButton={true}
              />
            </div>
          )}

          {lesson.videoFile && !lesson.videoId && (
            <div className="mb-3">
              <FileItem file={lesson.videoFile} onRemove={removeVideo} />
            </div>
          )}

          {((!lesson.videoId && !lesson.videoFile) ||
            originalProvider.type !== VideProviderInputType.upload) && (
              <UploadButton
                type="button"
                onClick={selectFiles}
                disabled={isUploading}
                style={{ opacity: isUploading ? 0.6 : 1 }}
              >
                <FontAwesomeIcon
                  icon={faCloudArrowUp}
                  style={{ marginRight: 8 }}
                />
                {isUploading ? "Enviando vídeo..." : "Adicionar vídeo"}
              </UploadButton>
            )}
        </Form>
      );
    }
    return null;
  }

  return (
    <>
      <Card className="mt-4">
        <Card.Body>
          <Header>
            <div className="title">Aula {index + 1}</div>
            <IconButtons>
              <button
                type="button"
                aria-label="Mover para cima"
                onClick={onMoveUp}
                disabled={isUploading}
                style={{
                  opacity: isUploading ? 0.5 : 1,
                  cursor: isUploading ? "not-allowed" : "pointer",
                }}
              >
                <FontAwesomeIcon icon={faChevronUp} />
              </button>
              <button
                type="button"
                aria-label="Mover para baixo"
                onClick={onMoveDown}
                disabled={isUploading}
                style={{
                  opacity: isUploading ? 0.5 : 1,
                  cursor: isUploading ? "not-allowed" : "pointer",
                }}
              >
                <FontAwesomeIcon icon={faChevronDown} />
              </button>
              <button
                type="button"
                aria-label="Duplicar"
                onClick={onDuplicate}
                disabled={isUploading}
                style={{
                  opacity: isUploading ? 0.5 : 1,
                  cursor: isUploading ? "not-allowed" : "pointer",
                }}
              >
                <Image src={Paste} alt="Duplicar" width={16} height={16} />
              </button>
              <button
                type="button"
                aria-label="Excluir"
                onClick={onDelete}
                disabled={isUploading}
                style={{
                  opacity: isUploading ? 0.5 : 1,
                  cursor: isUploading ? "not-allowed" : "pointer",
                }}
              >
                <Image src={Trash} alt="Excluir" width={16} height={16} />
              </button>
            </IconButtons>
          </Header>

          <SectionLabel>Informações gerais</SectionLabel>
          <Row className="g-3 gap-0">
            <Col xs={12}>
              <Form.Label className="fw-semibold">
                Nome da aula<span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                value={lesson.title}
                onChange={handleTitleChange}
                onBlur={handleTitleBlur}
                placeholder="Digite o nome da aula"
                isInvalid={touchedFields.title && !lesson.title?.trim()}
                disabled={isUploading}
              />
              <Form.Control.Feedback type="invalid">
                Campo obrigatório
              </Form.Control.Feedback>
            </Col>

            <Col xs={12} className="mt-0">
              <Input
                id="description"
                type="texteditor"
                code="texteditor"
                label="Descrição"
                value={lesson.description}
                disabled={isUploading}
                className={
                  touchedFields.description && !lesson.description?.trim()
                    ? "is-invalid"
                    : ""
                }
                onChange={(event) => {
                  handleDescriptionChange(event);
                }}
                onBlur={handleDescriptionBlur}
              />
              <Form.Control.Feedback type="invalid">
                Campo obrigatório
              </Form.Control.Feedback>
            </Col>
          </Row>

          <Divider />
          <SectionLabel className="mt-0 mb-0">Conteúdo da aula</SectionLabel>

          <Row className="g-3">
            <Col xs={12}>
              <VideoProviderRadio />
              <VideoProviderField />
              <input
                ref={filesUploadRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.bmp,.svg,.mp4,.avi,.mov,.wmv,.zip,.rar"
                multiple
                hidden
                onChange={(e) => onAttachmentFilesSelected(e.target.files)}
              />

              <div className="mt-3">
                <Form.Label className="fw-semibold">
                  Adicione Arquivos
                </Form.Label>

                {(lesson.attachments?.length ?? 0) > 0 && (
                  <div className="mb-3">
                    {lesson.attachments!.map((file, fileIndex) => (
                      <FileItem
                        key={`${file.name}-${fileIndex}`}
                        file={file}
                        onRemove={() => removeAttachment(fileIndex)}
                      />
                    ))}
                  </div>
                )}

                <UploadButton
                  type="button"
                  onClick={selectAttachmentFiles}
                  disabled={isUploading}
                  style={{ opacity: isUploading ? 0.6 : 1 }}
                >
                  <FontAwesomeIcon
                    icon={faCloudArrowUp}
                    style={{ marginRight: 8 }}
                  />
                  Adicionar arquivo
                </UploadButton>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="mt-3">
        {onAddLesson && (
          <AddLessonBar
            type="button"
            onClick={onAddLesson}
            disabled={isUploading}
            style={{
              opacity: isUploading ? 0.5 : 1,
              cursor: isUploading ? "not-allowed" : "pointer",
              pointerEvents: isUploading ? "none" : "auto",
            }}
          >
            <span style={{ fontWeight: 900, fontSize: 16, lineHeight: 1 }}>
              +
            </span>
            {isUploading ? "Enviando vídeo..." : "Adicionar aula"}
          </AddLessonBar>
        )}
      </div>
    </>
  );
};
